import { Injectable } from '@angular/core';
import { WooCommerceRestaurant } from '../../interfaces/woo-commerce/api/restaurant/restaurant-api.interface';

/**
 * Caches the mapped `/store/info` response per store keyed by the
 * `x-store-api-key`. This lets us seed the restaurant state instantly on
 * load / after a location switch so the UI does not flash an empty header
 * (logo, name, hero, working hours, etc.) while the HTTP request is in flight.
 *
 * The cached value is the already-mapped `WooCommerceRestaurant` produced by
 * `mapHappyStoreInfoToWooRestaurant` so we can hand it straight to the state
 * reducer without re-running the backend-shape mapping.
 */
@Injectable({ providedIn: 'root' })
export class HappyStoreInfoCacheService {
  private readonly KEY_PREFIX = 'happy:store-info:';

  get(storeApiKey: string | null | undefined): WooCommerceRestaurant | null {
    if (!storeApiKey) {
      return null;
    }

    try {
      const raw = window.localStorage.getItem(this.buildKey(storeApiKey));
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as WooCommerceRestaurant;
    } catch {
      return null;
    }
  }

  set(
    storeApiKey: string | null | undefined,
    payload: WooCommerceRestaurant
  ): void {
    if (!storeApiKey || !payload) {
      return;
    }

    try {
      window.localStorage.setItem(
        this.buildKey(storeApiKey),
        JSON.stringify(payload)
      );
    } catch {
      // Silently ignore quota/availability failures.
    }
  }

  remove(storeApiKey: string | null | undefined): void {
    if (!storeApiKey) {
      return;
    }

    try {
      window.localStorage.removeItem(this.buildKey(storeApiKey));
    } catch {
      // Silently ignore failures.
    }
  }

  private buildKey(storeApiKey: string): string {
    return `${this.KEY_PREFIX}${storeApiKey}`;
  }
}
